import { JOURS_MOIS_PAIE } from '@shared/constants/Taux';

/** Bornes calendaires d'une période de paie `AAAA-MM`. */
function bornesPeriode(periode: string): { debut: Date; fin: Date } | null {
  const m = /^(\d{4})-(\d{2})$/.exec(periode.trim());
  if (!m) return null;
  const annee = Number(m[1]);
  const mois = Number(m[2]);
  if (mois < 1 || mois > 12) return null;
  return { debut: new Date(Date.UTC(annee, mois - 1, 1)), fin: new Date(Date.UTC(annee, mois, 0)) };
}

function parseIso(date: string | undefined): Date | null {
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date.trim())) return null;
  const d = new Date(`${date.trim()}T00:00:00.000Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Jours payés du mois en trentièmes (règle usuelle au Gabon).
 *
 * Un contrat qui couvre tout le mois vaut 30 jours, quel que soit le nombre réel
 * de jours du calendrier (février compris). Une embauche ou une sortie en cours
 * de mois ne paie que les jours calendaires réellement couverts, plafonnés à 30.
 */
export function joursPayesDansMois(
  periode: string,
  dateEmbauche?: string,
  dateSortie?: string,
): number {
  const bornes = bornesPeriode(periode);
  if (!bornes) return JOURS_MOIS_PAIE;

  const embauche = parseIso(dateEmbauche);
  const sortie = parseIso(dateSortie);

  const debut = embauche && embauche > bornes.debut ? embauche : bornes.debut;
  const fin = sortie && sortie < bornes.fin ? sortie : bornes.fin;
  if (fin < debut) return 0;

  const moisComplet = debut.getTime() === bornes.debut.getTime() && fin.getTime() === bornes.fin.getTime();
  if (moisComplet) return JOURS_MOIS_PAIE;

  const jours = Math.round((fin.getTime() - debut.getTime()) / 86_400_000) + 1;
  return Math.max(0, Math.min(jours, JOURS_MOIS_PAIE));
}

/**
 * Le salarié doit-il figurer dans la saisie du mois ?
 *
 * On garde le salarié sorti pour son dernier bulletin (mois de la sortie) et on
 * écarte celui dont le contrat commence après la fin du mois.
 */
export function estPayableSurPeriode(
  salarie: { date_embauche: string; date_sortie?: string },
  periode: string,
): boolean {
  return joursPayesDansMois(periode, salarie.date_embauche, salarie.date_sortie) > 0;
}
